import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';

export interface BudgetDto {
  budgetId: string;
  accountingPeriodId: string;
  periodName: string;
  accountId: string;
  accountCode: string;
  accountNameAr: string;
  accountNameEn: string | null;
  costCenterId: string | null;
  costCenterName: string | null;
  amount: number;
  actual: number;
  variance: number;
}

export interface CreateBudgetInput {
  accountingPeriodId: string;
  accountId: string;
  costCenterId?: string | null;
  amount: number;
}

const QK = ['budgets'] as const;

export function useBudgets(periodId: string | null) {
  return useQuery({
    queryKey: [...QK, periodId] as const,
    enabled: !!periodId,
    queryFn: async () =>
      (await apiClient.get<BudgetDto[]>('/api/accounting/budgets', { params: { periodId } })).data,
  });
}

export function useCreateBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: CreateBudgetInput) =>
      (await apiClient.post<BudgetDto>('/api/accounting/budgets', req)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}

export function useUpdateBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, amount, costCenterId }: { id: string; amount: number; costCenterId: string | null }) => {
      await apiClient.put(`/api/accounting/budgets/${id}`, { amount, costCenterId });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}

export function useDeleteBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/accounting/budgets/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}
